import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer } from "@/components/SiteChrome";
import { BrushCallout, ArrowIndicator } from "@/components/BrushCallout";
import { ProductTile } from "@/components/ProductTile";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import api from "@/lib/api";

const CATEGORIES = ["Lighting", "Recovery", "Storage", "Armor", "Wheels & Tires", "Exterior"];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [bcForm, setBcForm] = useState({ name: "", email: "", rig: "", instagram: "", why: "" });
    const [bcBusy, setBcBusy] = useState(false);
    const [bcDone, setBcDone] = useState(false);

    useDocumentMeta({
        title: "Urban Off-Road — Overland gear built for the trail",
        description: "Armor, recovery, lighting, and overland gear engineered for Ford, Jeep, Toyota, and Chevy builds. Free shipping on parts over $250.",
        image: typeof window !== "undefined" ? `${window.location.origin}/assets/hero.jpg` : undefined,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        type: "website",
    });

    const submitBuildClub = async (e) => {
        e.preventDefault();
        setBcBusy(true);
        try {
            const { data } = await api.post("/build-club", bcForm);
            setBcDone(true);
            toast.success(data.duplicate ? "You're already on the list — we'll be in touch" : "You're in. We'll be in touch soon.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong. Try again?");
        } finally { setBcBusy(false); }
    };

    useEffect(() => {
        api.get("/products", { params: { limit: 200 } }).then(({ data }) => {
            setTotalCount(data.count);
            const withBadge = data.items.filter((p) => p.badge).slice(0, 4);
            setFeatured(withBadge.length > 0 ? withBadge : data.items.slice(0, 4));
        }).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="home-page">
            <Header />

            <section className="relative overflow-hidden border-b-2 border-[hsl(var(--ink))]">
                <div className="grain absolute inset-0" aria-hidden="true" />
                <div className="relative mx-auto grid max-w-7xl gap-x-10 gap-y-6 px-4 py-16 sm:px-6 md:grid-cols-12 md:py-24">
                    <div className="md:col-span-7 md:pt-6">
                        <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">
                            Gear up for your next off-road adventure
                        </div>
                        <h1 className="mt-6 font-display text-5xl uppercase italic leading-[0.95] sm:text-7xl md:text-[7rem] text-[hsl(var(--ink))]">
                            Discover
                            <br />
                            the thrill of
                        </h1>
                    </div>

                    <div className="relative md:col-span-5">
                        <div className="relative aspect-[4/3] overflow-hidden border-2 border-[hsl(var(--ink))]">
                            <img
                                src="/assets/hero-beyond-limits.png"
                                alt="Jeep Wrangler with rooftop tent driving through golden-hour desert dust"
                                className="h-full w-full object-cover object-center"
                            />
                        </div>
                    </div>

                    <div className="md:col-span-12 mt-4">
                        <BrushCallout className="-ml-1 text-[28px] sm:text-[48px] md:text-[62px] lg:text-[86px] xl:text-[118px]">Urban Off-Road</BrushCallout>
                    </div>

                    <div className="md:col-span-7 mt-6">
                        <p className="max-w-md text-base text-[hsl(var(--ink-soft))]">
                            Built parts for built rigs. Honest specs, real fitment, and the gear you'd actually pack
                            before driving past the pavement.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/shop" className="btn-primary inline-flex items-center gap-3" data-testid="hero-shop-cta">
                                Start exploring now <ArrowIndicator />
                            </Link>
                            <Link
                                to="/about"
                                className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))] underline underline-offset-8 decoration-2 hover:text-[hsl(var(--ink-soft))]"
                                data-testid="hero-about-cta"
                            >
                                Our story
                            </Link>
                        </div>
                        <div className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-[hsl(var(--dust-300))] pt-6">
                            {[
                                [`${totalCount || 240}+`, "Parts in stock"],
                                ["6", "Categories"],
                                ["48h", "Trail-ready ship"],
                            ].map(([v, l]) => (
                                <div key={l}>
                                    <div className="font-display text-3xl text-[hsl(var(--ink))]">{v}</div>
                                    <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                <div className="flex items-end justify-between gap-4">
                    <div>
                        <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Shop by category</div>
                        <h2 className="mt-2 font-display text-4xl uppercase italic md:text-5xl text-[hsl(var(--ink))]">Pick your fight.</h2>
                    </div>
                    <Link to="/shop" className="hidden font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))] underline underline-offset-8 md:inline">
                        View all
                    </Link>
                </div>
                <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                    {CATEGORIES.map((c, i) => (
                        <Link
                            key={c}
                            to={`/shop?category=${encodeURIComponent(c)}`}
                            className="group relative aspect-square overflow-hidden border border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] p-4 text-[hsl(var(--dust-50))] transition-transform hover:-translate-y-1"
                            data-testid={`home-category-${c.toLowerCase().replace(/\s|&/g, "")}`}
                        >
                            <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--dust-100))]">
                                0{i + 1}
                            </div>
                            <div className="absolute bottom-3 left-3 right-3 font-heading text-lg uppercase leading-tight">
                                {c}
                            </div>
                            <div className="absolute right-3 top-3 opacity-0 transition-opacity group-hover:opacity-100">
                                <ArrowIndicator />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <section className="border-y-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] text-[hsl(var(--dust-50))]">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <div className="mb-10 flex items-end justify-between">
                        <div>
                            <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--dust-100))]">New + on sale</div>
                            <h2 className="mt-2 font-display text-4xl uppercase italic md:text-5xl">Fresh in the bay.</h2>
                        </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {featured.map((p) => <ProductTile key={p.slug} p={p} />)}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
                <div className="relative overflow-hidden border-2 border-[hsl(var(--ink))] bg-[hsl(var(--ink))] p-8 md:p-12" data-testid="build-club-section">
                    <div className="grain absolute inset-0" aria-hidden="true" />
                    <div className="relative grid gap-8 md:grid-cols-2 md:items-start">
                        <div>
                            <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))]">
                                Now enrolling · Sponsored Build Club
                            </div>
                            <h3 className="mt-4 font-display text-4xl uppercase italic text-[hsl(var(--dust-50))] md:text-6xl">
                                Ride with us. Get geared.
                            </h3>
                            <p className="mt-6 max-w-md text-[hsl(var(--dust-100))]">
                                We're picking a small crew of builders who put their rigs through it. Perks:
                                early access, sponsored parts on select builds, and invites to trail runs.
                            </p>
                            <ul className="mt-6 space-y-2 text-sm text-[hsl(var(--dust-100))]">
                                {["Free gear on approved builds", "Early access to new drops", "Invites to closed trail runs", "Feature on the UOR journal"].map((x) => (
                                    <li key={x} className="flex items-start gap-2">
                                        <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))]" />
                                        <span>{x}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {bcDone ? (
                            <div className="border-2 border-[hsl(var(--primary))] p-8 text-[hsl(var(--dust-50))]" data-testid="build-club-done">
                                <div className="font-display text-4xl uppercase italic">You're on the list.</div>
                                <p className="mt-3 text-[hsl(var(--dust-100))]">We'll be in your inbox within a week with the next steps.</p>
                            </div>
                        ) : (
                            <form onSubmit={submitBuildClub} className="space-y-3 border-2 border-[hsl(var(--primary))]/40 p-6" data-testid="build-club-form">
                                <div className="grid grid-cols-2 gap-3">
                                    <input required placeholder="Name" value={bcForm.name} onChange={(e) => setBcForm({ ...bcForm, name: e.target.value })} className="border border-[hsl(var(--dust-50))]/30 bg-transparent px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--dust-50))] placeholder:text-[hsl(var(--dust-100))]/60 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]" data-testid="build-club-name" />
                                    <input required type="email" placeholder="Email" value={bcForm.email} onChange={(e) => setBcForm({ ...bcForm, email: e.target.value })} className="border border-[hsl(var(--dust-50))]/30 bg-transparent px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--dust-50))] placeholder:text-[hsl(var(--dust-100))]/60 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]" data-testid="build-club-email" />
                                </div>
                                <input required placeholder="Your rig (year/make/model)" value={bcForm.rig} onChange={(e) => setBcForm({ ...bcForm, rig: e.target.value })} className="w-full border border-[hsl(var(--dust-50))]/30 bg-transparent px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--dust-50))] placeholder:text-[hsl(var(--dust-100))]/60 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]" data-testid="build-club-rig" />
                                <input placeholder="Instagram / build page (optional)" value={bcForm.instagram} onChange={(e) => setBcForm({ ...bcForm, instagram: e.target.value })} className="w-full border border-[hsl(var(--dust-50))]/30 bg-transparent px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--dust-50))] placeholder:text-[hsl(var(--dust-100))]/60 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]" data-testid="build-club-instagram" />
                                <textarea rows={3} placeholder="What are you building? Where do you wheel?" value={bcForm.why} onChange={(e) => setBcForm({ ...bcForm, why: e.target.value })} className="w-full border border-[hsl(var(--dust-50))]/30 bg-transparent px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--dust-50))] placeholder:text-[hsl(var(--dust-100))]/60 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]" data-testid="build-club-why" />
                                <button
                                    type="submit"
                                    disabled={bcBusy}
                                    className="w-full border-2 border-[hsl(var(--primary))] bg-[hsl(var(--primary))] py-3 font-heading text-sm font-bold uppercase tracking-wider text-[hsl(var(--ink))] hover:bg-[hsl(var(--primary-hover))] disabled:opacity-60"
                                    data-testid="build-club-submit"
                                >
                                    {bcBusy ? "Sending…" : "Apply to join →"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <RecentlyViewed title="Pick up where you left off" />

            <Footer />
        </div>
    );
}
